// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { useState } from "react";

import type {
  CockpitApi,
  ConversationView,
  FinalReviewPreview,
} from "./api.js";
import { displayState } from "./presentation.js";

export function FinalReviewControls({
  api,
  conversations,
  busy,
  onBusy,
  onRefresh,
}: {
  readonly api: CockpitApi;
  readonly conversations: readonly ConversationView[];
  readonly busy: boolean;
  readonly onBusy: (busy: boolean) => void;
  readonly onRefresh: () => Promise<void>;
}) {
  const [prepared, setPrepared] = useState<{
    readonly preview: FinalReviewPreview;
    readonly conversationRevision: number;
    readonly actionId: string;
  } | null>(null);
  const [message, setMessage] = useState("");
  const [failed, setFailed] = useState(false);
  const previews = conversations.flatMap((view) =>
    (view.finalReviewPreviews ?? []).map((preview) => ({
      preview,
      conversationRevision: view.conversation.revision,
    })),
  );
  const current =
    prepared !== null &&
    previews.some(
      ({ preview, conversationRevision }) =>
        preview.payloadDigest === prepared.preview.payloadDigest &&
        conversationRevision === prepared.conversationRevision,
    );
  const decisions = conversations.flatMap((view) =>
    view.deliveryIntents.flatMap((intent) =>
      intent.finalReviewActionId === undefined
        ? []
        : [{ ...intent, conversationId: view.conversation.conversationId }],
    ),
  );

  async function dispatch(conversationId: string, intentId: string) {
    const { intent } = await api.dispatchReviewProjection(
      conversationId,
      intentId,
    );
    setFailed(intent.state !== "delivered");
    setMessage(
      intent.state === "delivered"
        ? "Request changes published to GitHub."
        : `Request changes is ${displayState(intent.state).toLowerCase()}. ${intent.lastErrorCode ?? "Delivery has not completed."}`,
    );
  }

  async function submit() {
    if (!current) return;
    onBusy(true);
    setFailed(false);
    setMessage("Authorizing this review…");
    try {
      const { preview } = prepared;
      const result = await api.reviewAction(preview.conversationId, {
        action: {
          kind: "decide-final-review",
          decision: "request-changes",
          previewDigest: preview.payloadDigest,
          target: {
            adapterId: preview.adapterId,
            targetReference: preview.targetReference,
          },
        },
        actionId: prepared.actionId,
        expectedConversationRevision: prepared.conversationRevision,
        source: "cockpit",
      });
      if (
        result.action.state !== "completed" ||
        result.action.outcomeCode !== "collaboration.final-review-authorized" ||
        result.action.resultReference === null
      ) {
        setFailed(true);
        setMessage(
          `Request changes was not authorized: ${result.action.outcomeCode}. Refresh and inspect the current preview.`,
        );
        setPrepared(null);
      } else {
        setPrepared(null);
        setMessage("Review authorized. Sending to GitHub…");
        await dispatch(preview.conversationId, result.action.resultReference);
      }
    } catch (error) {
      setFailed(true);
      setMessage(
        `${error instanceof Error ? error.message : String(error)} Check the delivery status below before retrying.`,
      );
    } finally {
      await onRefresh();
      onBusy(false);
    }
  }

  async function retry(conversationId: string, intentId: string) {
    onBusy(true);
    setFailed(false);
    setMessage("Checking the authorized review delivery…");
    try {
      await dispatch(conversationId, intentId);
    } catch (error) {
      setFailed(true);
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      await onRefresh();
      onBusy(false);
    }
  }

  return (
    <section className="disposition-panel" aria-labelledby="final-review-title">
      <h3 id="final-review-title">GitHub review decision</h3>
      {previews.length === 0 ? (
        <p className="hint">
          Request changes is unavailable. It requires current blocking evidence
          and a supported GitHub target.
        </p>
      ) : (
        <div className="stack">
          {previews.map(({ preview, conversationRevision }) => (
            <button
              className="button secondary"
              type="button"
              disabled={busy}
              key={preview.payloadDigest}
              onClick={() => {
                setPrepared({
                  preview,
                  conversationRevision,
                  actionId: `cockpit-action:${crypto.randomUUID()}`,
                });
                setMessage("");
                setFailed(false);
              }}
            >
              Preview Request changes · {preview.targetReference}
            </button>
          ))}
        </div>
      )}
      {prepared === null ? null : (
        <div className="finding-card" aria-label="Request changes preview">
          <p>Target: {prepared.preview.targetReference}</p>
          <p className="hint">Commit: {prepared.preview.headRevision}</p>
          <pre className="review-publication-preview">
            {prepared.preview.body}
          </pre>
          {!current ? (
            <p role="alert">
              This preview has changed. Select the current preview before
              submitting.
            </p>
          ) : null}
          <div className="button-row">
            <button
              className="button danger"
              type="button"
              disabled={busy || !current}
              onClick={() => void submit()}
            >
              Submit Request changes
            </button>
            <button
              className="button tertiary"
              type="button"
              disabled={busy}
              onClick={() => {
                setPrepared(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {decisions.map((intent) => (
        <div className="finding-card" key={intent.intentId}>
          <p>Request changes: {displayState(intent.state)}</p>
          {intent.lastErrorCode === null ? null : (
            <p className="hint">{intent.lastErrorCode}</p>
          )}
          {["pending", "retry", "delivering"].includes(intent.state) ? (
            <button
              className="button secondary"
              type="button"
              disabled={busy}
              onClick={() => void retry(intent.conversationId, intent.intentId)}
            >
              Check and send authorized review
            </button>
          ) : null}
        </div>
      ))}
      <p role={failed ? "alert" : "status"}>{message}</p>
    </section>
  );
}
